import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AutenticacaoService } from '../seguranca/autenticacao.service';
import { CustomerDebtRenegociationData } from '../shared/models/customer-debt-renegociation';
import { ParametroAdm } from '../shared/models/parametro-adm-model';
import { ServicoCartaoIndisponivelError } from '../shared/models/servico-cartao-indisponivel.error';
import BrowserUtils from '../shared/utils/browser-utils';
import { AcordoCliente, Divida, OfertaNegociacao, ParcelasCartao } from './../shared/models/customer-debt-renegociation';
import { ContratarAcordoRequest } from './contratar-acordo-request';
import { AcordoResponse } from './contratar-acordo-response';
import { ApiService } from '../shared/api/api.service';

export enum PermissaoRenegociacaoDominioEnum {
  CARTAO_BRADESCO_CLIENTE = 'cartaoBradescoCliente',
  CARTAO_BRADESCO_COLABORADOR = 'cartaoBradescoColaborador',
}


const SERVICO_INDISPONIVEL_HTTP_CODE = 412;

@Injectable({
  providedIn: "root",
})
export class RenegociacaoCartaoService {
  dadosRenegociacaoCartao: CustomerDebtRenegociationData;
  debitoAtual: Divida;
  ofertaAtualEscolhida: OfertaNegociacao;
  acordoVigente: AcordoCliente;
  parcelaSelecionada: ParcelasCartao;

  constructor(
    private http: HttpClient,
    private autenticacaoService: AutenticacaoService,
    private apiService: ApiService
  ) { }

  async buscarDadosRenegociacaoCartao(isHome?: boolean): Promise<CustomerDebtRenegociationData> {
    try {
      const path = this.apiService.getEndpoint({
        apiKey: 'cdc-portal-negociacao-cartao-bradesco',
        path: '/negociacao-cartao-bradesco/customers/debts',
        oldBrowserFallbackPath: '/customers/debts'
      })

      if (!isHome && this.dadosRenegociacaoCartao) {
        return this.dadosRenegociacaoCartao;
      }

      const dadosRenegociacaoCartao = await this.http.get<CustomerDebtRenegociationData>(path).toPromise();
      this.dadosRenegociacaoCartao = dadosRenegociacaoCartao;

      return dadosRenegociacaoCartao;
    } catch (err) {
      this.handleErrorServicoIndisponivel(err);
    }
  }

  get apiUrlService(): string {
    if (BrowserUtils.isOldFirefox()) {
      return `${environment.cdccorpApiURLService}`;
    }

    return `${environment.apiURLService}`;
  }

  salvarDebitIdAtual(debitId: number): void {
    window.sessionStorage.setItem("debitId", debitId.toString());
  }

  private getDebitIdAtual(): string {
    return window.sessionStorage.getItem("debitId");
  }

  async debtAtual(): Promise<Divida> {
    if (this.debitoAtual) {
      return this.debitoAtual;
    }

    try {
      await this.buscarDadosRenegociacaoCartao();
      const debitId = this.getDebitIdAtual();
      this.debitoAtual = this.findDebtAtualByDebitId(Number(debitId));

      return this.debitoAtual;
    } catch (err) {
      this.handleErrorServicoIndisponivel(err);
    }
  }

  findDebtAtualByDebitId(debitId: number): Divida {
    return this.dadosRenegociacaoCartao.dividasCliente[0].dividas.find(
      (debt) => debt.idDividaBradesco === debitId
    );
  }

  salvarOfertaEscolhidaId(ofertaEscolhida: number): void {
    window.sessionStorage.setItem(
      "ofertaEscolhida",
      ofertaEscolhida.toString()
    );
  }

  buscarOfertaEscolhidaId(): string {
    return window.sessionStorage.getItem("ofertaEscolhida");
  }

  async ofertaEscolhida(): Promise<OfertaNegociacao> {
    if (this.ofertaAtualEscolhida) {
      return this.ofertaAtualEscolhida;
    } else {
      await this.buscarDadosRenegociacaoCartao();
      const debitId = this.getDebitIdAtual();

      this.debitoAtual = this.findDebtAtualByDebitId(Number(debitId));
      const ofertaEscolhidaId = this.buscarOfertaEscolhidaId();
      return this.debitoAtual.ofertasNegociacao.find(
        (offer) => offer.idOferta === Number(ofertaEscolhidaId)
      );
    }
  }

  salvarParcelaSelecionada(parcela: ParcelasCartao): void {
    window.sessionStorage.setItem("nrParcelaSelecionada", parcela.nrParcela.toString());
  }

  buscarParcelaSelecionada(): ParcelasCartao {
    if (this.parcelaSelecionada) {
      return this.parcelaSelecionada;
    } else {
      const nrParcelaSelecionada = window.sessionStorage.getItem("nrParcelaSelecionada");
      this.parcelaSelecionada =
        this.dadosRenegociacaoCartao.acordosCliente[0].parcelas.find(
          (parcela) => parcela.nrParcela === Number(nrParcelaSelecionada)
        );
      return this.parcelaSelecionada;
    }
  }

  async buscarAcordoVigente(debtId?: string, ischeckOut?: boolean): Promise<AcordoCliente> {
    if (!ischeckOut && this.acordoVigente) {
      return this.acordoVigente;
    }

    if (debtId && !this.dadosRenegociacaoCartao) {
      await this.buscarDadosRenegociacaoCartao();
    }

    if (debtId) {
      this.acordoVigente = this.dadosRenegociacaoCartao.acordosCliente.find((acordo) => acordo.idDividaBradesco.toString() === debtId);
    } else {
      this.acordoVigente = this.dadosRenegociacaoCartao.acordosCliente[0];
    }

    return this.acordoVigente;
  }

  async enviarAcordo(acordo: ContratarAcordoRequest): Promise<AcordoResponse> {
    try {
      const path = this.apiService.getEndpoint({
        apiKey: 'cdc-portal-negociacao-cartao-bradesco',
        path: '/negociacao-cartao-bradesco/customers/agreements',
        oldBrowserFallbackPath: '/customers/agreements'
      })
      const res = await this.http.post<AcordoResponse>(path, acordo).toPromise();
      return res;
    } catch (error) {
      this.handleErrorServicoIndisponivel(error);
    }
  }

  limparDadosDivida() {
    this.dadosRenegociacaoCartao = null;
    this.debitoAtual = null;
    this.ofertaAtualEscolhida = null;
    this.acordoVigente = null;
    this.parcelaSelecionada = null;
  }

  async downloadBoleto() {
    try {
      const boleto = await this.pegarBoleto();
      const byteCharacters = atob(boleto.boleto);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "Boleto.pdf";
      link.click();
    } catch (err) {
      this.handleErrorServicoIndisponivel(err);
    }
  }

  async pegarBoleto(): Promise<any> {
    try {
      const path = this.apiService.getEndpoint({
        apiKey: 'cdc-portal-negociacao-cartao-bradesco',
        path: '/negociacao-cartao-bradesco/customers/billet',
        oldBrowserFallbackPath: '/customers/billet'
      })

      let queryParams = new HttpParams()
        .set("idAcordo", this.acordoVigente.idAcordo.toString())
        .set("nrParcela", this.parcelaSelecionada.nrParcela.toString());
      const res = await this.http.get(path, { params: queryParams }).toPromise();

      return res;
    } catch (error) {
      return this.handleErrorServicoIndisponivel(error);
    }
  }

  async enviarEmailBoleto(): Promise<any> {
    try {
      const path = this.apiService.getEndpoint({
        apiKey: 'cdc-portal-negociacao-cartao-bradesco',
        path: '/negociacao-cartao-bradesco/customers/billet/mail',
        oldBrowserFallbackPath: '/customers/billet/mail'
      })

      const body = {
        idAcordo: this.acordoVigente.idAcordo,
        nrParcela: this.parcelaSelecionada.nrParcela
      };

      const res = await this.http.post(path, body).toPromise();
      return res;
    } catch (error) {
      return this.handleErrorServicoIndisponivel(error);
    }
  }

  async verificarPermissaoRenegociacaoCartao(): Promise<boolean> {
    try {
      const dominio = this.autenticacaoService.temColaboradorAutenticado()
        ? PermissaoRenegociacaoDominioEnum.CARTAO_BRADESCO_COLABORADOR
        : PermissaoRenegociacaoDominioEnum.CARTAO_BRADESCO_CLIENTE;

      const path = this.apiService.getEndpoint({
        apiKey: 'cdc-portal-negociacao-cartao-bradesco',
        path: `/negociacao-cartao-bradesco/parameter/domain/${dominio}`,
        oldBrowserFallbackPath: `/parameter/domain/${dominio}`,
      })

      const [res] = await this.http.get<ParametroAdm[]>(path).toPromise();
      return res?.valor?.toLowerCase() === 'true';
    } catch (error) {
      this.handleErrorServicoIndisponivel(error);
    }
  }

  private handleErrorServicoIndisponivel(error: HttpErrorResponse): never {
    if (error.status === SERVICO_INDISPONIVEL_HTTP_CODE) {
      throw new ServicoCartaoIndisponivelError(error);
    }
    throw error;
  }
}
